// Routes
export const ROUTE_PATHS = {
  HOME: '/',
};

// Types
export interface ScenarioData {
  salesPrice: number;
  settlementDate: string;
  // Pre-Marketing Expenses
  appraisal: number;
  inspections: number;
  repairsUpfits: number;
  preMarketingOther1: number;
  preMarketingOther2: number;
  // Mortgages and Liens
  mortgage1: number;
  mortgage2: number;
  homeEquityLoan: number;
  hoaAssessment: number;
  fixtureLiens: number;
  otherLien1: number;
  otherLien2: number;
  // Closing Expenses
  attorneyFee: number;
  listingCommissionPct: number;
  listingComplianceFee: number;
  buyerAgentCommissionPct: number;
  sellerConcessions: number;
  homeWarranty: number;
  miscFees: number;
  proRatedHoaDues: number;
  hoaFeesCertification: number;
  proRatedTaxes: number;
  recordingFees: number;
  repairsFromInspection: number;
  closingOther1: number;
  closingOther2: number;
  // Bottom
  dueDiligenceFeeReceived: number;
}

export interface Listing {
  id: string;
  sellerName: string;
  property: string;
  createdAt: string;
  scenarios: [ScenarioData, ScenarioData, ScenarioData];
}

// Calculations
export function calcListingCompensation(salesPrice: number, pct: number): number {
  return salesPrice * (pct / 100);
}

export function calcBuyerAgentCompensation(salesPrice: number, pct: number): number {
  return salesPrice * (pct / 100);
}

export function calcRevenueStamps(salesPrice: number): number {
  return Math.floor(salesPrice / 500) * 1;
}

export function calcTotalExpenses(s: ScenarioData): number {
  const preMarketing = s.appraisal + s.inspections + s.repairsUpfits + s.preMarketingOther1 + s.preMarketingOther2;
  const mortgages = s.mortgage1 + s.mortgage2 + s.homeEquityLoan + s.hoaAssessment + s.fixtureLiens + s.otherLien1 + s.otherLien2;
  const listingComp = calcListingCompensation(s.salesPrice, s.listingCommissionPct);
  const buyerComp = calcBuyerAgentCompensation(s.salesPrice, s.buyerAgentCommissionPct);
  const revenueStamps = calcRevenueStamps(s.salesPrice);
  const closing =
    s.attorneyFee +
    listingComp +
    s.listingComplianceFee +
    buyerComp +
    s.sellerConcessions +
    s.homeWarranty +
    s.miscFees +
    s.proRatedHoaDues +
    s.hoaFeesCertification +
    s.proRatedTaxes +
    s.recordingFees +
    s.repairsFromInspection +
    revenueStamps +
    s.closingOther1 +
    s.closingOther2;
  return preMarketing + mortgages + closing;
}

export function calcNetProceeds(s: ScenarioData): number {
  return s.salesPrice - calcTotalExpenses(s) + s.dueDiligenceFeeReceived;
}

// Default scenario
export function defaultScenario(salesPrice = 0): ScenarioData {
  return {
    salesPrice,
    settlementDate: '',
    appraisal: 0,
    inspections: 0,
    repairsUpfits: 0,
    preMarketingOther1: 0,
    preMarketingOther2: 0,
    mortgage1: 0,
    mortgage2: 0,
    homeEquityLoan: 0,
    hoaAssessment: 0,
    fixtureLiens: 0,
    otherLien1: 0,
    otherLien2: 0,
    attorneyFee: 400,
    listingCommissionPct: 2.5,
    listingComplianceFee: 350,
    buyerAgentCommissionPct: 2.5,
    sellerConcessions: 0,
    homeWarranty: 0,
    miscFees: 100,
    proRatedHoaDues: 0,
    hoaFeesCertification: 0,
    proRatedTaxes: 0,
    recordingFees: 150,
    repairsFromInspection: 0,
    closingOther1: 0,
    closingOther2: 0,
    dueDiligenceFeeReceived: 0,
  };
}

export function defaultListing(): Listing {
  return {
    id: crypto.randomUUID(),
    sellerName: '',
    property: '',
    createdAt: new Date().toISOString(),
    scenarios: [defaultScenario(), defaultScenario(), defaultScenario()],
  };
}

// Format currency
export function formatCurrency(val: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(val);
}

// Format percent
export function formatPct(val: number): string {
  return val.toFixed(2) + '%';
}
